// Website signups and website messages — shared frontend vocabulary.
//
// These MIRROR backend/website_engagement.py. The server is the source of
// truth and revalidates everything; this file only drives the UI so we never
// offer a status or preference the backend would reject.

import { badgeClass, formatDate, formatDateTime } from './applications';

export { badgeClass, formatDate, formatDateTime };

// Shown wherever an optional field is empty. The rule across both screens is
// that a blank field is displayed as "Not provided" rather than hidden, so the
// shape of a record never changes depending on what the visitor filled in.
export const NOT_PROVIDED = 'Not provided';

// `tone` drives the generic admin badge; `pillTone` picks a colour from the
// shared Agencies pill palette so a signup status reads the same as an agency
// status of the same temperature.
export const SIGNUP_STATUS_META = {
  new: { label: 'New', tone: 'warning', pillTone: 'gold' },
  contacted: { label: 'Contacted', tone: 'neutral', pillTone: 'neutral' },
  confirmed: { label: 'Confirmed', tone: 'success', pillTone: 'emerald' },
  attended: { label: 'Attended', tone: 'success', pillTone: 'emerald' },
  no_show: { label: 'No show', tone: 'danger', pillTone: 'red' },
  cancelled: { label: 'Cancelled', tone: 'danger', pillTone: 'slate' },
};

export const SIGNUP_STATUSES = Object.keys(SIGNUP_STATUS_META);

// What the status dropdown may set. "cancelled" is deliberately absent: it is
// reached through the Cancel action, which releases the seat and records who
// cancelled and why. Picking it from a dropdown did none of that and left the
// event holding a seat nobody was coming to, so the server now refuses it.
export const SIGNUP_STATUS_CHOICES = SIGNUP_STATUSES.filter(
  (status) => status !== 'cancelled',
);

/** The statuses to offer given what the record is now.
 *
 * A cancelled registration still has to render its own status, so it keeps
 * "Cancelled" in the list — picking anything else reinstates it and takes the
 * seat back.
 */
export function signupStatusChoices(currentStatus) {
  return currentStatus === 'cancelled'
    ? ['cancelled', ...SIGNUP_STATUS_CHOICES]
    : SIGNUP_STATUS_CHOICES;
}

export const ATTENDANCE_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'undecided', label: 'Undecided' },
];

export const HEARD_ABOUT_OPTIONS = [
  { value: 'google', label: 'Google / search' },
  { value: 'social_media', label: 'Social media' },
  { value: 'friend_or_family', label: 'Friend or family' },
  { value: 'agency', label: 'Agency' },
  { value: 'event', label: 'Event or fair' },
  { value: 'other', label: 'Other' },
];

export const CHAT_STATUS_META = {
  new: { label: 'New', tone: 'warning' },
  open: { label: 'Open', tone: 'success' },
  pending: { label: 'Waiting on visitor', tone: 'neutral' },
  closed: { label: 'Closed', tone: 'neutral' },
};

export const CHAT_STATUSES = Object.keys(CHAT_STATUS_META);

// Poll windows. Vercel functions cannot hold a socket open, so both screens
// poll; the heartbeat cadence has to stay under the server's 90s online window.
export const CONVERSATION_POLL_MS = 8000;
export const MESSAGE_POLL_MS = 6000;
export const HEARTBEAT_MS = 30000;

export function signupStatusMeta(status) {
  return SIGNUP_STATUS_META[status]
    || { label: status || 'Unknown', tone: 'neutral', pillTone: 'neutral' };
}

export function chatStatusMeta(status) {
  return CHAT_STATUS_META[status] || { label: status || 'Unknown', tone: 'neutral' };
}

export function attendanceLabel(value) {
  const option = ATTENDANCE_OPTIONS.find((item) => item.value === value);
  return option ? option.label : displayValue(value);
}

/**
 * How a lead found us, including the free-text wording the visitor typed when
 * they picked something we do not have an option for.
 */
export function heardAboutLabel(value, detail = '') {
  const option = HEARD_ABOUT_OPTIONS.find((item) => item.value === value);
  const wording = String(detail || '').trim();
  if (!option) return displayValue(value);
  if (option.value === 'other' && wording) return `Other — ${wording}`;
  return option.label;
}

/** Any value rendered in a read-only field: never blank, never hidden. */
export function displayValue(value) {
  if (value === null || value === undefined) return NOT_PROVIDED;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const text = String(value).trim();
  return text || NOT_PROVIDED;
}

export function fullName(record) {
  const name = `${record?.first_name || ''} ${record?.last_name || ''}`.trim();
  return name || record?.full_name || '';
}

/**
 * A source page as something readable: the path, with the host only when the
 * lead came from somewhere other than our own site.
 */
export function sourcePageLabel(value) {
  const text = String(value || '').trim();
  if (!text) return NOT_PROVIDED;
  try {
    const url = new URL(text);
    const path = `${url.pathname}${url.search}`.replace(/\/$/, '') || '/';
    return url.hostname.endsWith('metropolitancollege.ca') ? path : `${url.hostname}${path}`;
  } catch {
    return text;
  }
}

/** Short "when did this happen" for the inbox list. */
export function relativeTime(value, now = Date.now()) {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(value);
}

/** Merge polled messages into what is on screen without dropping or duplicating. */
export function mergeMessages(current = [], incoming = []) {
  const byId = new Map();
  [...(current || []), ...(incoming || [])].forEach((message) => {
    if (message?.id) byId.set(message.id, message);
  });
  return [...byId.values()].sort((a, b) =>
    String(a.created_at || '').localeCompare(String(b.created_at || '')));
}

// ─── Contact inquiries ───
// MIRRORS backend/website_engagement.py INQUIRY_* — the server revalidates
// everything, so this exists only so the UI never offers a status the backend
// would refuse.

export const INQUIRY_STATUS_META = {
  new: { label: 'New', tone: 'warning', pillTone: 'gold' },
  open: { label: 'Open', tone: 'success', pillTone: 'emerald' },
  replied: { label: 'Replied', tone: 'success', pillTone: 'emerald' },
  closed: { label: 'Closed', tone: 'neutral', pillTone: 'slate' },
  spam: { label: 'Spam', tone: 'danger', pillTone: 'red' },
};

export const INQUIRY_STATUSES = Object.keys(INQUIRY_STATUS_META);

// What the status control may set. "replied" is deliberately absent: it means
// an email actually reached this person, and the only thing entitled to write
// it is a send that succeeded — the server refuses it from here.
export const INQUIRY_STATUS_CHOICES = INQUIRY_STATUSES.filter(
  (status) => status !== 'replied',
);

/** The statuses to offer given what the record is now.
 *
 * An inquiry that has been replied to still has to render its own status, so
 * it keeps "Replied" in the list — picking anything else moves it on. */
export function inquiryStatusChoices(currentStatus) {
  return currentStatus === 'replied'
    ? ['replied', ...INQUIRY_STATUS_CHOICES]
    : INQUIRY_STATUS_CHOICES;
}

export const INQUIRY_SUBJECT_OPTIONS = [
  { value: 'admissions', label: 'Admissions' },
  { value: 'programs', label: 'Programs' },
  { value: 'tuition', label: 'Tuition & fees' },
  { value: 'agency', label: 'Agency partnership' },
  { value: 'events', label: 'Events' },
  { value: 'support', label: 'Student support' },
  { value: 'other', label: 'Other' },
];

export function inquiryStatusMeta(status) {
  return INQUIRY_STATUS_META[status]
    || { label: status || 'Unknown', tone: 'neutral', pillTone: 'neutral' };
}

/**
 * What an inquiry is about, in the visitor's own words when they wrote about
 * something we do not have a category for. The server sends `subject_label`
 * already resolved; this is the fallback for a row that predates it.
 */
export function inquirySubjectLabel(subject, original = '') {
  const wording = String(original || '').trim();
  const option = INQUIRY_SUBJECT_OPTIONS.find((item) => item.value === subject);
  if (subject === 'other' && wording) return wording;
  return option ? option.label : (wording || 'Other');
}

// ─── Newsletter subscribers ───

export const SUBSCRIBER_STATUS_META = {
  active: { label: 'Active', tone: 'success', pillTone: 'emerald' },
  unsubscribed: { label: 'Unsubscribed', tone: 'neutral', pillTone: 'slate' },
};

export const SUBSCRIBER_STATUSES = Object.keys(SUBSCRIBER_STATUS_META);

export function subscriberStatusMeta(status) {
  return SUBSCRIBER_STATUS_META[status]
    || { label: status || 'Unknown', tone: 'neutral', pillTone: 'neutral' };
}

export const LOCALE_LABELS = { en: 'English', fr: 'Français' };

export function localeLabel(value) {
  const code = String(value || '').trim();
  return LOCALE_LABELS[code] || (code ? code.toUpperCase() : NOT_PROVIDED);
}

/**
 * One timeline entry's icon key. The timeline mixes what the visitor did with
 * what staff did, so each kind reads differently at a glance rather than as
 * one undifferentiated list.
 */
export const ACTIVITY_KIND_LABELS = {
  received: 'Received',
  replied: 'Replied',
  status_changed: 'Status changed',
  assigned: 'Assigned',
  unassigned: 'Unassigned',
  note_added: 'Note added',
  read_state: 'Read state',
};

export function activityKindLabel(kind) {
  return ACTIVITY_KIND_LABELS[kind] || 'Activity';
}
